// The Timeline card: one Highcharts chart with stacked panes on a shared 15-minute x axis (action strip,
// no-export strip, prices, battery, power, ventilation, CO2), a pinned slot readout driven by hover / touch
// instead of a floating tooltip, a range control and drag / pinch zoom with our own reset button.
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/pattern-fill';

import { EnergyAirQuality, EnergyPlanSnapshot, VentLevel } from '../../common';
import { LayoutSize } from '../util';
import { VENT_NAMES, ventIndex } from '../util/air';
import {
  ACTIONS,
  Action,
  COLORS,
  CONTROLLER_FLOOR_PERCENT,
  HOUR_MS,
  Row,
  SLOT_MS,
  slotLabel,
} from '../util/energy_model';
import { hhmm, num1 } from '../util/format';
import { localMinuteOfDay, tallinnParts } from '../util/tallinn_time';
import {
  ChartView,
  Crosshair,
  LEVEL_LABELS,
  PaneTitle,
  TALLINN_TIME,
  ZoomHandlers,
  hideHighchartsResetZoom,
  nudgeEdgeLabels,
  paneAxis,
  ppmLabel,
  useChartPointer,
  useWindowZoom,
} from './ui/chart';
import { Card } from './ui/card';
import { ResetZoomButton, Segmented } from './ui/controls';
import { CARD_BG, CO2_COLORS, FAINT, HAIRLINE_STRONG, INK, MUTED, PAGE_BG, SECONDARY } from './ui/tokens';

type RangeKey = '12' | '24' | 'all';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: '12', label: '12 h' },
  { key: '24', label: '24 h' },
  { key: 'all', label: 'All' },
];

// ---- Geometry -------------------------------------------------------------------------------------

type PaneKey = 'action' | 'noExport' | 'price' | 'soc' | 'power' | 'vent' | 'co2';

interface Geometry {
  left: number; // plot left = room for the y labels
  top: number;
  bottom: number;
  height: number;
  font: number;
  panes: Record<PaneKey, { top: number; height: number }>;
}

// Pane heights and the gap after each (px), from the prototypes.
const PANES: Record<LayoutSize, { left: number; top: number; font: number; panes: [PaneKey, number, number][] }> = {
  phone: {
    left: 26,
    top: 2,
    font: 10,
    panes: [
      ['action', 14, 5],
      ['noExport', 6, 8],
      ['price', 100, 8],
      ['soc', 72, 8],
      ['power', 110, 8],
      ['vent', 40, 8],
      ['co2', 64, 0],
    ],
  },
  tablet: {
    left: 32,
    top: 2,
    font: 10,
    panes: [
      ['action', 16, 6],
      ['noExport', 8, 8],
      ['price', 110, 8],
      ['soc', 80, 8],
      ['power', 110, 8],
      ['vent', 44, 8],
      ['co2', 60, 0],
    ],
  },
  web: {
    left: 40,
    top: 16, // room for the "now" label
    font: 11,
    panes: [
      ['action', 16, 4],
      ['noExport', 6, 8],
      ['price', 170, 8],
      ['soc', 110, 8],
      ['power', 170, 8],
      ['vent', 60, 8],
      ['co2', 90, 0],
    ],
  },
};

function geometry(size: LayoutSize, hasAir: boolean): Geometry {
  const spec = PANES[size];
  const panes = {} as Geometry['panes'];
  let y = spec.top;
  const list = spec.panes.filter(([key]) => hasAir || key !== 'co2');
  list.forEach(([key, h, gap], i) => {
    panes[key] = { top: y, height: h };
    y += h + (i < list.length - 1 ? gap : 0);
  });
  if (!hasAir) {
    panes.co2 = { top: y, height: 0 };
  }
  const bottom = 22;
  return { left: spec.left, top: spec.top, bottom, height: y + bottom, font: spec.font, panes };
}

// ---- Data helpers ---------------------------------------------------------------------------------

const KW_PER_SLOT_KWH = HOUR_MS / SLOT_MS;
const slotKw = (kwh: number | undefined) => (kwh === undefined ? undefined : kwh * KW_PER_SLOT_KWH);

// Step lines need a closing point at the end of the last slot to draw it.
function stepData(rows: Row[], value: (r: Row) => number | null | undefined): [number, number | null][] {
  const data: [number, number | null][] = [];
  let prevEnd: number | undefined;
  for (const r of rows) {
    const v = value(r);
    if (v === undefined) {
      continue;
    }
    if (prevEnd !== undefined && r.start > prevEnd) {
      data.push([prevEnd, null]); // gap
    }
    data.push([r.start, v]);
    prevEnd = r.start + SLOT_MS;
  }
  if (data.length > 0 && prevEnd !== undefined) {
    data.push([prevEnd, data[data.length - 1][1]]);
  }
  return data;
}

const midData = (rows: Row[], value: (r: Row) => number | undefined): [number, number][] =>
  rows.filter((r) => value(r) !== undefined).map((r) => [r.start + SLOT_MS / 2, value(r) as number]);

// Starts a plan series at the last actual point so the forecast continues from it across the slot in
// progress (actuals end at the last finished 5-minute interval, the plan's first point is mid-slot).
function bridged(actual: [number, number | null][], plan: [number, number][]): [number, number][] {
  for (let i = actual.length - 1; i >= 0; i--) {
    const [x, v] = actual[i];
    if (v !== null) {
      // (Not across a telemetry outage: only from within a slot of the plan's first point.)
      return plan.length > 0 && plan[0][0] - x <= SLOT_MS ? [[x, v], ...plan.filter((p) => p[0] > x)] : plan;
    }
  }
  return plan;
}

function estimatedZones(rows: Row[], solid: string, estimatedColor: string): Highcharts.SeriesZonesOptionsObject[] {
  const style = (estimated: boolean): Highcharts.SeriesZonesOptionsObject =>
    estimated ? { dashStyle: 'ShortDash', color: estimatedColor } : { dashStyle: 'Solid', color: solid };
  const zones: Highcharts.SeriesZonesOptionsObject[] = [];
  let current = false;
  for (const r of rows) {
    const estimated = !!r.estimatedPrice;
    if (estimated !== current) {
      zones.push({ ...style(current), value: r.start });
      current = estimated;
    }
  }
  zones.push(style(current));
  return zones;
}

// Forecast PV: diagonal stripes in the solar colour on a light tint of it.
const PV_FORECAST_FILL: Highcharts.PatternObject = {
  pattern: {
    path: { d: 'M 0 6 L 6 0 M -1 1 L 1 -1 M 5 7 L 7 5', stroke: 'rgba(237,161,0,0.7)', strokeWidth: 1.25 },
    width: 6,
    height: 6,
    backgroundColor: 'rgba(237,161,0,0.1)',
  },
};
const PV_FORECAST_SWATCH = 'repeating-linear-gradient(135deg, #f1b94a 0 1.5px, #fdf3e0 1.5px 4px)';

// `color` mixed toward the card background (past slots of the action strip).
function fade(hex: string, amount = 0.55): string {
  const c = (h: string, i: number) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  const mix = [0, 1, 2].map((i) => Math.round(c(hex, i) * (1 - amount) + c(CARD_BG, i) * amount));
  return `rgb(${mix.join(',')})`;
}

// slot start -> CO2 slot mean per room.
function co2BySlot(air: EnergyAirQuality | undefined): Map<number, { name: string; co2: number }[]> {
  const m = new Map<number, { name: string; co2: number }[]>();
  for (const room of air?.rooms ?? []) {
    for (const s of room.slots) {
      m.set(s.start, [...(m.get(s.start) ?? []), { name: room.name, co2: s.co2 }]);
    }
  }
  return m;
}

// Hours between x labels for a visible span (hours).
function tickHours(size: LayoutSize, spanH: number): number {
  if (size === 'phone') {
    return spanH <= 6 ? 1 : spanH <= 14 ? 2 : 6;
  }
  if (size === 'tablet') {
    return spanH <= 6 ? 1 : spanH <= 26 ? 2 : 4;
  }
  return spanH <= 4 ? 0.5 : spanH <= 10 ? 1 : spanH <= 24 ? 2 : 3;
}

// ---- Chart options ----------------------------------------------------------------------------------

function buildOptions(
  plan: EnergyPlanSnapshot,
  rows: Row[],
  now: number,
  size: LayoutSize,
  g: Geometry,
  initial: [number, number],
  handlers: React.MutableRefObject<ZoomHandlers>,
  showSpot: boolean,
): Highcharts.Options {
  const air = plan.airQuality;
  const hasAir = (air?.rooms ?? []).some((r) => r.slots.length > 0);
  const pastRows = rows.filter((r) => r.past);
  const planRows = rows.filter((r) => !r.past);
  const power = plan.power;
  const byStart = new Map<number, Row>(rows.map((r) => [r.start, r]));
  const fontSize = `${g.font}px`;

  const socPlan: [number, number][] = [];
  if (plan.slots.length > 0) {
    socPlan.push([plan.slots[0].start, plan.slots[0].socStartPercent]);
    for (const s of plan.slots) {
      socPlan.push([s.start + SLOT_MS, s.socEndPercent]);
    }
  }
  const socActual: [number, number | null][] = power
    ? power.socPercent.map((v, i) => [power.start + (i + 1) * power.stepMs, v])
    : pastRows.filter((r) => r.socEndPercent !== undefined).map((r) => [r.start + SLOT_MS, r.socEndPercent as number]);
  // Actual power in kW: 5-minute means where the controller sent them, else the 15-minute slot mean (older
  // controllers, and the time before the 5-minute history started).
  const pastKw = (column: 'loadW' | 'pvW', kwh: (r: Row) => number | undefined): [number, number | null][] => {
    if (!power) {
      return midData(pastRows, (r) => slotKw(kwh(r)));
    }
    return power[column].map((w, i) => {
      const t = power.start + i * power.stepMs;
      if (w !== null) {
        return [t + power.stepMs / 2, w / 1000];
      }
      const row = byStart.get(Math.floor(t / SLOT_MS) * SLOT_MS);
      return [t + power.stepMs / 2, row?.past ? (slotKw(kwh(row)) ?? null) : null];
    });
  };
  const pvActual = pastKw('pvW', (r) => r.pvKwh);
  const loadActual = pastKw('loadW', (r) => r.loadKwh);

  const floorPercent = plan.socFloorPercent ?? CONTROLLER_FLOOR_PERCENT;
  const socPlotLines: Highcharts.YAxisPlotLinesOptions[] = [
    { value: floorPercent, color: COLORS.floor, dashStyle: 'ShortDash', width: 1, zIndex: 3 },
  ];
  if (plan.chargeWindows?.gridChargeEnabled) {
    socPlotLines.push({
      value: plan.chargeWindows.targetSocPercent,
      color: '#9a9993',
      dashStyle: 'Dot',
      width: 1,
      zIndex: 3,
      label: {
        text: `target ${plan.chargeWindows.targetSocPercent}%`,
        align: 'right',
        x: -2,
        y: -3,
        style: { fontSize: '9px', color: MUTED },
      },
    });
  }

  const pane = (key: PaneKey) => ({ top: g.panes[key].top, height: g.panes[key].height });
  const axisBase = (key: PaneKey): Highcharts.YAxisOptions => paneAxis(pane(key), g.font);
  const strip = (key: PaneKey, extra: Highcharts.YAxisOptions = {}): Highcharts.YAxisOptions => ({
    ...axisBase(key),
    min: 0,
    max: 1,
    tickPositions: [],
    gridLineWidth: 0,
    labels: { enabled: false },
    ...extra,
  });
  const zeroLine: Highcharts.YAxisPlotLinesOptions = { value: 0, color: COLORS.zero, width: 1, zIndex: 2 };

  // CO2: 400 up to the high threshold + 200 (more if measured); compact ticks on narrow screens.
  const th = air?.thresholds ?? { good: 1000, high: 1200 };
  const co2Max = Math.max(
    th.high + 200,
    Math.ceil(Math.max(0, ...(air?.rooms ?? []).flatMap((r) => r.slots.map((s) => s.co2))) / 200) * 200,
  );
  const co2Ticks =
    size === 'web'
      ? Array.from({ length: (co2Max - 400) / 200 + 1 }, (_, i) => 400 + i * 200)
      : [400, th.good, th.high, co2Max];

  const series: Highcharts.SeriesOptionsType[] = [
    // Action strip: one column per slot; past slots faded.
    {
      type: 'column',
      id: 'action',
      yAxis: 0,
      pointRange: SLOT_MS,
      pointPadding: 0,
      data: rows
        .filter((r) => r.action !== undefined)
        .map((r) => ({
          x: r.start + SLOT_MS / 2,
          y: 1,
          color: r.past ? fade(ACTIONS[r.action!].color) : ACTIONS[r.action!].color,
        }))
        // A border in the column's own colour covers the anti-aliasing seams between slots.
        .map((p) => ({ ...p, borderColor: p.color })),
      borderWidth: 0.5,
    },
    {
      type: 'column',
      id: 'no-export',
      yAxis: 1,
      color: COLORS.noExport,
      borderColor: COLORS.noExport,
      borderWidth: 0.5,
      pointRange: SLOT_MS,
      pointPadding: 0,
      data: planRows.filter((r) => r.curtailExport).map((r) => [r.start + SLOT_MS / 2, 1]),
    },
    // Prices
    {
      type: 'line',
      id: 'import-past',
      yAxis: 2,
      step: 'left',
      color: 'rgba(42,120,214,0.45)',
      data: stepData(pastRows, (r) => r.importPrice),
    },
    {
      type: 'area',
      id: 'import-plan',
      yAxis: 2,
      step: 'left',
      color: COLORS.importPrice,
      fillOpacity: 0.08,
      threshold: null,
      zoneAxis: 'x',
      zones: estimatedZones(planRows, COLORS.importPrice, COLORS.importPriceEstimated),
      data: stepData(planRows, (r) => r.importPrice),
    },
    {
      type: 'line',
      id: 'export-past',
      yAxis: 2,
      step: 'left',
      color: 'rgba(27,175,122,0.45)',
      lineWidth: 1,
      data: stepData(pastRows, (r) => r.exportPrice),
    },
    {
      type: 'line',
      id: 'export-price',
      yAxis: 2,
      step: 'left',
      color: COLORS.exportPrice,
      lineWidth: 1,
      data: stepData(planRows, (r) => r.exportPrice),
    },
    // Nord Pool spot (excl VAT), off by default.
    {
      type: 'line',
      id: 'spot',
      yAxis: 2,
      step: 'left',
      color: COLORS.spot,
      lineWidth: 1.25,
      visible: showSpot,
      zoneAxis: 'x',
      zones: estimatedZones(rows, COLORS.spot, MUTED),
      data: stepData(rows, (r) => r.spot),
    },
    // Battery
    {
      type: 'area',
      id: 'soc-plan',
      yAxis: 3,
      color: COLORS.socPlan,
      fillOpacity: 0.1,
      data: socPlan,
    },
    { type: 'line', id: 'soc-actual', yAxis: 3, color: COLORS.socActual, data: socActual },
    // Power: grid columns (import up, export down; slot means like the plan's, not 5-minute ones), PV area,
    // load line.
    {
      type: 'column',
      id: 'grid-actual',
      yAxis: 4,
      color: 'rgba(227,73,72,0.45)',
      negativeColor: 'rgba(27,175,122,0.75)',
      pointRange: SLOT_MS,
      pointPadding: 0.15,
      data: midData(pastRows, (r) => slotKw(r.gridKwh)),
    },
    {
      type: 'column',
      id: 'grid-plan',
      yAxis: 4,
      color: 'rgba(227,73,72,0.45)',
      negativeColor: 'rgba(27,175,122,0.75)',
      pointRange: SLOT_MS,
      pointPadding: 0.15,
      data: midData(planRows, (r) => slotKw(r.gridKwh)),
    },
    {
      type: 'area',
      id: 'pv-actual',
      yAxis: 4,
      color: COLORS.pv,
      fillOpacity: 0.3,
      lineWidth: 0,
      data: pvActual,
    },
    {
      type: 'area',
      id: 'pv-plan',
      yAxis: 4,
      color: COLORS.pv,
      fillColor: PV_FORECAST_FILL,
      fillOpacity: 1,
      lineWidth: 0,
      data: bridged(
        pvActual,
        midData(planRows, (r) => slotKw(r.pvKwh)),
      ),
    },
    { type: 'line', id: 'load-actual', yAxis: 4, color: COLORS.load, data: loadActual },
    {
      type: 'line',
      id: 'load-plan',
      yAxis: 4,
      color: COLORS.load,
      dashStyle: 'ShortDash',
      data: bridged(
        loadActual,
        midData(planRows, (r) => slotKw(r.loadKwh)),
      ),
    },
    // Ventilation level (low / mid / high = 1..3)
    {
      type: 'area',
      id: 'vent',
      yAxis: 5,
      step: 'left',
      color: COLORS.vent,
      fillOpacity: 0.14,
      threshold: null,
      data: stepData(rows, (r) => ventIndex(r.ventLevel)),
    },
  ];
  if (hasAir) {
    air!.rooms.forEach((room, i) => {
      series.push({
        type: 'line',
        id: `co2-${room.name}`,
        yAxis: 6,
        color: CO2_COLORS[i % CO2_COLORS.length],
        data: room.slots.map((s): [number, number] => [s.start + SLOT_MS / 2, s.co2]),
      });
    });
  }

  return {
    chart: {
      height: g.height,
      marginLeft: g.left,
      marginRight: 2,
      marginTop: g.top,
      marginBottom: g.bottom,
      spacing: [0, 0, 0, 0],
      backgroundColor: 'transparent',
      animation: false,
      style: { fontFamily: 'inherit' },
      selectionMarkerFill: 'rgba(42,120,214,0.12)',
      zooming: { type: 'x', pinchType: 'x' },
      events: {
        load: hideHighchartsResetZoom,
        render() {
          // (Not the compact CO2 axis' top label: pushed down it would sit on the high threshold's.)
          nudgeEdgeLabels(this, g.font, (axis) => axis.options.id === 'co2' && size !== 'web');
        },
      },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
    time: TALLINN_TIME,
    xAxis: {
      type: 'datetime',
      min: initial[0],
      max: initial[1],
      minRange: HOUR_MS,
      lineColor: HAIRLINE_STRONG,
      tickLength: 0,
      tickPositioner() {
        const min = this.min ?? initial[0];
        const max = this.max ?? initial[1];
        const every = tickHours(size, (max - min) / HOUR_MS) * 60;
        const positions: number[] = [];
        for (let t = Math.ceil(min / 1_800_000) * 1_800_000; t <= max; t += 1_800_000) {
          if (localMinuteOfDay(t) % every === 0) {
            positions.push(t);
          }
        }
        const info = {
          unitName: 'hour',
          unitRange: every * 60_000,
          count: 1,
          higherRanks: [],
          boundaryTicks: [] as string[],
          totalRange: max - min,
        };
        return Object.assign(positions, { info });
      },
      labels: {
        y: g.font + 6,
        style: { fontSize, color: MUTED },
        formatter() {
          const t = this.value as number;
          if (localMinuteOfDay(t) === 0) {
            return `<span style="font-weight:700">${tallinnParts(t).weekday}</span>`;
          }
          return size === 'phone' ? hhmm(t).slice(0, 2) : hhmm(t);
        },
      },
      plotLines: [
        {
          value: now,
          color: COLORS.now,
          width: 2,
          zIndex: 5,
          label:
            size === 'web'
              ? {
                  text: 'now',
                  rotation: 0,
                  align: 'center',
                  x: 0,
                  y: -4,
                  verticalAlign: 'top',
                  style: { fontSize: '11px', fontWeight: '600', color: COLORS.now },
                }
              : undefined,
        },
      ],
      events: {
        afterSetExtremes(e) {
          handlers.current.onExtremes(e.min, e.max);
        },
      },
    },
    yAxis: [
      strip('action'),
      strip('noExport', { plotBands: [{ from: 0, to: 1, color: PAGE_BG }] }),
      {
        ...axisBase('price'),
        softMin: 0,
        softMax: 40,
        endOnTick: true,
        maxPadding: 0.02,
        tickPixelInterval: 30,
        plotLines: [zeroLine],
      },
      {
        ...axisBase('soc'),
        min: 0,
        max: 100,
        tickPositions: size === 'web' ? [0, 25, 50, 75, 100] : [0, 50, 100],
        plotLines: socPlotLines,
      },
      {
        ...axisBase('power'),
        softMin: 0,
        softMax: 1,
        endOnTick: true,
        maxPadding: 0.02,
        tickPixelInterval: 28,
        allowDecimals: false,
        plotLines: [zeroLine],
      },
      {
        ...axisBase('vent'),
        min: 0.6,
        max: 3.4,
        tickPositions: [1, 2, 3],
        gridLineWidth: 0,
        labels: {
          ...axisBase('vent').labels,
          style: { ...(axisBase('vent').labels!.style as Highcharts.CSSObject), fontSize: `${g.font - 1}px` },
          formatter() {
            return LEVEL_LABELS[this.value as number] ?? '';
          },
        },
      },
      ...(hasAir
        ? [
            {
              ...axisBase('co2'),
              id: 'co2',
              min: 400,
              max: co2Max,
              tickPositions: co2Ticks,
              gridLineWidth: size === 'web' ? 1 : 0,
              labels: {
                ...axisBase('co2').labels,
                formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                  return ppmLabel(this.value as number, size !== 'web');
                },
              },
              plotLines: [
                { value: th.good, color: COLORS.co2Good, dashStyle: 'ShortDash', width: 1, zIndex: 3 },
                { value: th.high, color: COLORS.co2High, dashStyle: 'ShortDash', width: 1, zIndex: 3 },
              ],
            } as Highcharts.YAxisOptions,
          ]
        : []),
    ],
    plotOptions: {
      series: {
        animation: false,
        lineWidth: 1.75,
        marker: { enabled: false },
        enableMouseTracking: false,
        states: { hover: { enabled: false }, inactive: { enabled: false } },
      },
      // borderRadius: square slots (Highcharts 11+ rounds columns by default, notching the strips between slots).
      column: { grouping: false, groupPadding: 0, borderWidth: 0, borderRadius: 0, crisp: false },
      area: { lineWidth: 1.75 },
    },
    series,
  };
}

// ---- Slot readout -----------------------------------------------------------------------------------

interface Readout {
  title: string;
  action?: Action;
  price: string;
  exportPrice: string;
  spot?: string; // only while the spot series is shown
  soc: string;
  load: string;
  pv: string;
  gridLabel: 'Import' | 'Export';
  grid: string;
  vent: string;
  temp: string;
  co2Long: string;
  co2Short: string;
}

function readoutFor(
  row: Row | undefined,
  isNow: boolean,
  co2: Map<number, { name: string; co2: number }[]>,
  showSpot: boolean,
): Readout {
  const dash = '—';
  const kw = (kwh: number | undefined) => (kwh === undefined ? dash : `${(kwh * KW_PER_SLOT_KWH).toFixed(1)} kW`);
  const rooms = row ? co2.get(row.start) : undefined;
  const exporting = row?.gridKwh !== undefined && row.gridKwh * KW_PER_SLOT_KWH < -0.05;
  return {
    title: row ? `${isNow ? 'Now' : row.past ? 'Actual' : 'Plan'} · ${slotLabel(row.start)}` : 'Now',
    action: row?.action,
    price: row?.importPrice !== undefined ? `${row.importPrice.toFixed(1)} c` : dash,
    exportPrice:
      row?.exportPrice !== undefined ? `${num1(row.exportPrice)} c${row.curtailExport ? ' · blocked' : ''}` : dash,
    spot: showSpot ? (row?.spot !== undefined ? `${num1(row.spot)} c` : dash) : undefined,
    soc:
      row?.socEndPercent === undefined
        ? dash
        : `${row.socStartPercent !== undefined ? Math.round(row.socStartPercent) : ''}→${Math.round(row.socEndPercent)}%`,
    load: kw(row?.loadKwh),
    pv: kw(row?.pvKwh),
    gridLabel: exporting ? 'Export' : 'Import',
    grid: row?.gridKwh === undefined ? dash : `${Math.abs(row.gridKwh * KW_PER_SLOT_KWH).toFixed(1)} kW`,
    vent: row?.ventLevel ? VENT_NAMES[row.ventLevel as VentLevel] : dash,
    temp: row?.outdoorTempC !== undefined ? `${num1(row.outdoorTempC)} °C` : dash,
    co2Long: rooms
      ? `${rooms.map((r) => `${r.name} ${Math.round(r.co2)}`).join(' · ')} ppm`
      : `${dash} (measured only)`,
    co2Short: rooms ? `${rooms.map((r) => Math.round(r.co2)).join(' / ')} ppm` : dash,
  };
}

const cell: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };

function ActionChip(props: { action?: Action; fontSize: number }) {
  if (!props.action) {
    return null;
  }
  return (
    <span
      style={{
        fontSize: props.fontSize,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 6,
        background: ACTIONS[props.action].tint,
        color: INK,
        whiteSpace: 'nowrap',
      }}
    >
      {ACTIONS[props.action].label}
    </span>
  );
}

const V = (props: { children: React.ReactNode }) => <b style={{ color: INK }}>{props.children}</b>;

// Phone: a box with a fixed grid of cells. Every line is always there, so its height never changes.
function ReadoutBox(props: { r: Readout; hasAir: boolean }) {
  const { r } = props;
  return (
    <div
      aria-live="polite"
      style={{
        background: PAGE_BG,
        borderRadius: 12,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, height: 20 }}>
        <span className="num" style={{ ...cell, fontSize: 13, fontWeight: 600 }}>
          {r.title}
        </span>
        <ActionChip action={r.action} fontSize={11} />
      </div>
      <div
        className="num"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '6px 10px',
          fontSize: 12,
          color: SECONDARY,
        }}
      >
        <span style={cell}>
          Price <V>{r.price}</V>
        </span>
        <span style={cell}>
          SoC <V>{r.soc}</V>
        </span>
        <span style={cell}>
          Vent <V>{r.vent}</V>
        </span>
        <span style={cell}>
          Load <V>{r.load}</V>
        </span>
        <span style={cell}>
          Solar <V>{r.pv}</V>
        </span>
        <span style={cell}>
          {r.gridLabel} <V>{r.grid}</V>
        </span>
      </div>
      <div
        className="num"
        style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, color: SECONDARY }}
      >
        <span style={cell}>
          Export price <V>{r.exportPrice}</V>
        </span>
        {r.spot !== undefined && (
          <span style={cell}>
            Spot <V>{r.spot}</V>
          </span>
        )}
        <span style={cell}>
          Outdoor <V>{r.temp}</V>
        </span>
      </div>
      {props.hasAir && (
        <span className="num" style={{ ...cell, fontSize: 12, color: SECONDARY }}>
          CO₂ {r.co2Long}
        </span>
      )}
    </div>
  );
}

// Tablet / web: one wrapping line of fields in a box of fixed (two-line) height.
function ReadoutLine(props: { r: Readout; hasAir: boolean; style?: React.CSSProperties }) {
  const { r } = props;
  return (
    <div
      aria-live="polite"
      className="num"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        alignContent: 'center',
        gap: '4px 14px',
        height: 56,
        boxSizing: 'border-box',
        overflow: 'hidden',
        background: PAGE_BG,
        borderRadius: 12,
        padding: '6px 14px',
        fontSize: 12,
        color: SECONDARY,
        whiteSpace: 'nowrap',
        ...props.style,
      }}
    >
      <b style={{ fontSize: 13, color: INK }}>{r.title}</b>
      <ActionChip action={r.action} fontSize={11} />
      <span>
        Price <V>{r.price}</V>
      </span>
      <span>
        Export price <V>{r.exportPrice}</V>
      </span>
      {r.spot !== undefined && (
        <span>
          Spot <V>{r.spot}</V>
        </span>
      )}
      <span>
        SoC <V>{r.soc}</V>
      </span>
      <span>
        Load <V>{r.load}</V>
      </span>
      <span>
        Solar <V>{r.pv}</V>
      </span>
      <span>
        {r.gridLabel} <V>{r.grid}</V>
      </span>
      <span>
        Vent <V>{r.vent}</V>
      </span>
      <span>
        Outdoor <V>{r.temp}</V>
      </span>
      {props.hasAir && (
        <span>
          CO₂ <V>{r.co2Short}</V>
        </span>
      )}
    </div>
  );
}

// ---- Legend -----------------------------------------------------------------------------------------

function Legend(props: {
  air: EnergyAirQuality | undefined;
  hasOutage?: boolean; // a past slot in view was a grid outage
  hasAir: boolean;
  size: LayoutSize;
  showSpot: boolean;
  onToggleSpot: () => void;
}) {
  const swatch = (bg: string, extra: React.CSSProperties = {}) => (
    <span style={{ width: 10, height: 10, borderRadius: 2, background: bg, flex: 'none', ...extra }} />
  );
  const line = (bg: string, h = 2) => <span style={{ width: 12, height: h, background: bg, flex: 'none' }} />;
  const dashed = (c: string, extra: React.CSSProperties = {}) => (
    <span style={{ width: 12, borderTop: `2px dashed ${c}`, flex: 'none', ...extra }} />
  );
  const items: [React.ReactNode, string][] = [
    [swatch(ACTIONS.charge.color), 'Charge'],
    [swatch(ACTIONS.export.color), 'Export'],
    [swatch(ACTIONS.hold.color), 'Hold'],
    [swatch(ACTIONS.auto.color, { boxShadow: 'inset 0 0 0 1px #d6d5ce' }), 'Self Use'],
    [swatch(ACTIONS.discharge.color), 'Discharge'],
    ...(props.hasOutage ? [[swatch(ACTIONS.outage.color), 'Grid outage'] as [React.ReactNode, string]] : []),
    [swatch('#f1a4a3'), 'Grid import'],
    [swatch(COLORS.gridExport), 'Grid export (below 0)'],
    [
      <span style={{ width: 12, height: 4, borderRadius: 2, background: COLORS.noExport, flex: 'none' }} />,
      'No export',
    ],
    [line(COLORS.importPrice), 'Import price'],
    [line(COLORS.exportPrice, 1), 'Export price'],
    [line(COLORS.load), 'Load'],
    [swatch('#f8e3b3'), 'Solar'],
    [swatch(PV_FORECAST_SWATCH), 'Solar forecast'],
  ];
  if (props.hasAir) {
    props.air!.rooms.forEach((room, i) => items.push([line(CO2_COLORS[i % CO2_COLORS.length]), room.name]));
  }
  const web = props.size === 'web';
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: web ? '6px 16px' : '6px 12px',
        fontSize: web ? 12 : 11,
        color: SECONDARY,
        paddingLeft: web ? 40 : 2,
      }}
    >
      {items.map(([mark, label]) => (
        <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {mark}
          {label}
        </span>
      ))}
      {props.hasAir && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {dashed(COLORS.co2Good)}
          {props.air!.thresholds.good} ppm
          {dashed(COLORS.co2High, { marginLeft: 6 })}
          {props.air!.thresholds.high} ppm
        </span>
      )}
      {/* The one optional series: hidden until its legend item is clicked. */}
      <button
        type="button"
        aria-pressed={props.showSpot}
        onClick={props.onToggleSpot}
        title="Nord Pool spot price, c/kWh excl VAT (as elektrikell.ee shows it)"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: 0,
          border: 'none',
          background: 'none',
          font: 'inherit',
          cursor: 'pointer',
          color: props.showSpot ? SECONDARY : FAINT,
        }}
      >
        {line(props.showSpot ? COLORS.spot : '#c3c2b7', 1.25)}
        Nord Pool spot
      </button>
      <span>Solid = actual · dashed / striped = forecast{web ? ' · hover to inspect a slot · drag to zoom' : ''}</span>
    </div>
  );
}

// ---- Card -------------------------------------------------------------------------------------------

function rangeWindow(range: RangeKey, now: number, dataMin: number, dataMax: number): [number, number] {
  if (range === 'all') {
    return [dataMin, dataMax];
  }
  const [before, after] = range === '12' ? [4, 12] : [6, 24];
  return [Math.max(dataMin, now - before * HOUR_MS), Math.min(dataMax, now + after * HOUR_MS)];
}

const PANE_TITLES: [PaneKey, string][] = [
  ['price', 'Price c/kWh'],
  ['soc', 'Battery %'],
  ['power', 'Power kW'],
  ['co2', 'CO₂ ppm'],
];

export function TimelineCard(props: {
  plan: EnergyPlanSnapshot;
  rows: Row[];
  now: number;
  size: LayoutSize;
  style?: React.CSSProperties;
}): React.ReactElement {
  const { plan, rows, now, size } = props;
  const [range, setRange] = useState<RangeKey>('24');
  const [showSpot, setShowSpot] = useState(false);
  const [chart, setChart] = useState<Highcharts.Chart | null>(null);
  const { sel, setSel, wrapRef, handlers: pointer } = useChartPointer(chart); // sel: x under the pointer

  const hasAir = (plan.airQuality?.rooms ?? []).some((r) => r.slots.length > 0);
  const g = useMemo(() => geometry(size, hasAir), [size, hasAir]);
  const dataMin = rows.length > 0 ? rows[0].start : now - 24 * HOUR_MS;
  const dataMax = rows.length > 0 ? rows[rows.length - 1].start + SLOT_MS : now + 24 * HOUR_MS;
  const win = rangeWindow(range, now, dataMin, dataMax);
  const { zoom, resetZoom, handlers, winRef } = useWindowZoom(chart, win, () => setSel(null));

  const options = useMemo(
    () => buildOptions(plan, rows, now, size, g, winRef.current, handlers, showSpot),
    [plan, rows, now, size, g, showSpot],
  );
  const onChart = useCallback((c: Highcharts.Chart) => setChart(c), []);

  const pick = (r: RangeKey) => {
    setRange(r);
    resetZoom();
    setSel(null);
  };

  const byStart = useMemo(() => new Map(rows.map((r) => [r.start, r])), [rows]);
  const co2 = useMemo(() => co2BySlot(plan.airQuality), [plan.airQuality]);
  const nowSlot = Math.floor(now / SLOT_MS) * SLOT_MS;
  const selSlot = sel !== null ? Math.floor(sel / SLOT_MS) * SLOT_MS : nowSlot;
  const selRow = byStart.get(selSlot);
  const readout = selRow
    ? readoutFor(selRow, selSlot === nowSlot, co2, showSpot)
    : readoutFor(byStart.get(nowSlot), true, co2, showSpot);

  const zoomLabel = zoom ? `zoomed to ${tallinnParts(zoom[0]).weekday} ${hhmm(zoom[0])}–${hhmm(zoom[1])}` : '';
  const phone = size === 'phone';
  const web = size === 'web';

  const rangeControl = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
      {zoom && (
        <ResetZoomButton
          size={size}
          onClick={() => {
            resetZoom();
            setSel(null);
          }}
        />
      )}
      <Segmented label="Range" options={RANGES} value={zoom ? null : range} onPick={pick} size={size} />
    </div>
  );

  return (
    <Card
      label="Timeline"
      style={{ display: 'flex', flexDirection: 'column', gap: web ? 12 : 10, minWidth: 0, ...props.style }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: web ? '10px 16px' : '8px 10px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: phone ? 'column' : 'row',
            alignItems: phone ? 'flex-start' : 'baseline',
            gap: phone ? 0 : 8,
          }}
        >
          <span style={{ fontSize: web ? 16 : phone ? 14 : 15, fontWeight: 600 }}>Timeline</span>
          {zoomLabel && (
            <span className="num" style={{ fontSize: phone ? 11 : 12, color: SECONDARY, whiteSpace: 'nowrap' }}>
              {zoomLabel}
            </span>
          )}
        </div>
        {web && <ReadoutLine r={readout} hasAir={hasAir} style={{ order: 2, flex: '1 1 640px', minWidth: 0 }} />}
        <div style={{ order: 3, marginLeft: 'auto', display: 'flex' }}>{rangeControl}</div>
      </div>
      {phone && <ReadoutBox r={readout} hasAir={hasAir} />}
      {size === 'tablet' && <ReadoutLine r={readout} hasAir={hasAir} />}

      <div ref={wrapRef} style={{ position: 'relative', cursor: 'crosshair' }} {...pointer}>
        <ChartView options={options} onChart={onChart} />
        {PANE_TITLES.filter(([key]) => key !== 'co2' || hasAir).map(([key, text]) => (
          <PaneTitle key={key} left={g.left} top={g.panes[key].top} font={g.font} web={web}>
            {key === 'soc' && web ? `${text} · floor ${plan.socFloorPercent ?? CONTROLLER_FLOOR_PERCENT}` : text}
          </PaneTitle>
        ))}
        <Crosshair chart={chart} x={sel} />
      </div>

      <Legend
        air={plan.airQuality}
        hasAir={hasAir}
        hasOutage={rows.some((r) => r.action === 'outage')}
        size={size}
        showSpot={showSpot}
        onToggleSpot={() => setShowSpot((v) => !v)}
      />
    </Card>
  );
}
